import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, ShieldCheck, Heart } from 'lucide-react';
import { heroContent, companyInfo, trustBadges } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const AdvancedHero = () => {
  const { t, language } = useLanguage();

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-navy-50 via-white to-eucalyptus-50/30 pt-32 pb-20 overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-20 w-96 h-96 bg-eucalyptus-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-navy-200/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div className="relative z-10">
            {/* Trust indicator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-eucalyptus-50 border border-eucalyptus-200 rounded-full mb-8"
            >
              <div className="w-2 h-2 bg-eucalyptus-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-eucalyptus-800">
                {t('Associated Aged Care Provider · Serving NSW', '关联养老服务商 · 服务新南威尔士州')}
              </span>
            </motion.div>

            {/* Main title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-navy-900 leading-[1.1] mb-6 tracking-tight"
            >
              {language === 'zh' ? (
                <>
                  在家享受<br />
                  <span className="text-eucalyptus-600">充满关怀</span>的<br />
                  专业照护
                </>
              ) : (
                <>
                  Care that feels<br />
                  like <span className="text-eucalyptus-600">family</span>,<br />
                  at home.
                </>
              )}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg sm:text-xl text-gray-600 mb-10 leading-relaxed max-w-xl"
            >
              {t(
                'Bilingual Support at Home & aged care services designed for Chinese families across Greater Sydney. Delivered with warmth, dignity and cultural understanding.',
                '专为大悉尼地区华人家庭设计的双语居家养老与护理服务。以温暖、尊严和文化理解，用心陪伴您的家人。'
              )}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-12"
            >
              <a
                href="#contact"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-navy-900 hover:bg-navy-800 text-white rounded-full text-base font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]"
              >
                {t('Book Free Consultation', '预约免费咨询')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center gap-3 px-8 py-4 bg-white hover:bg-gray-50 text-navy-900 rounded-full text-base font-semibold transition-all duration-300 border-2 border-gray-200 hover:border-navy-300"
              >
                <Phone className="w-5 h-5 text-eucalyptus-600" />
                {companyInfo.phone}
              </a>
            </motion.div>

            {/* Trust markers */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-wrap gap-6 pt-8 border-t border-gray-200"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-eucalyptus-600" />
                <span className="text-sm font-medium text-gray-700">
                  {t('Fully Insured', '全额保险')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-eucalyptus-600" />
                <span className="text-sm font-medium text-gray-700">
                  {t('Bilingual Team', '双语团队')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-eucalyptus-600" />
                <span className="text-sm font-medium text-gray-700">
                  {t('Police Checked', '警察背景调查')}
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={heroContent.image}
                alt="Compassionate aged care"
                className="w-full h-full object-cover"
              />
              {/* Floating card - Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20, x: -20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-5 shadow-xl"
              >
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    <div className="w-10 h-10 rounded-full bg-eucalyptus-100 border-2 border-white flex items-center justify-center text-eucalyptus-700 font-bold text-sm">Y</div>
                    <div className="w-10 h-10 rounded-full bg-navy-100 border-2 border-white flex items-center justify-center text-navy-700 font-bold text-sm">L</div>
                    <div className="w-10 h-10 rounded-full bg-eucalyptus-200 border-2 border-white flex items-center justify-center text-eucalyptus-800 font-bold text-sm">W</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-navy-900">100+ {t('families', '家庭')}</div>
                    <div className="text-xs text-gray-600">{t('trust AU YH Care', '信赖 AU YH Care')}</div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Floating accent badge - top right */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1, type: 'spring' }}
              className="absolute -top-6 -right-6 bg-eucalyptus-500 text-white rounded-2xl p-4 shadow-xl rotate-3"
            >
              <div className="text-2xl font-bold">EN · 中文</div>
              <div className="text-xs opacity-90">{t('Bilingual Service', '双语服务')}</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AdvancedHero;
